import { type FC, useState, useRef, useEffect } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { format, subDays, startOfQuarter, startOfYear, startOfDay, endOfDay } from 'date-fns';
import { CalendarIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import 'react-day-picker/dist/style.css';

type DateRangePickerProps = {
    value: DateRange | undefined;
    onChange: (range: DateRange | undefined) => void;
    presets?: boolean;
};

const DateRangePicker: FC<DateRangePickerProps> = ({value, onChange, presets = true}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [range, setRange] = useState<DateRange | undefined>(value);
    const containerRef = useRef<HTMLDivElement>(null);

    const today = new Date();

    const quickPresets = [
        { label: 'Today', range: { from: startOfDay(today), to: endOfDay(today) } },
        { label: 'Last 7 days', range: { from: startOfDay(subDays(today, 6)), to: endOfDay(today) } },
        { label: 'Last 30 days', range: { from: startOfDay(subDays(today, 29)), to: endOfDay(today) } },
        { label: 'This quarter', range: { from: startOfDay(startOfQuarter(today)), to: endOfDay(today) } },
        { label: 'This year', range: { from: startOfDay(startOfYear(today)), to: endOfDay(today) } },
    ];

    useEffect(() => {
        setRange(value);
    }, [value]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const normalizeRange = (dateRange: DateRange | undefined): DateRange | undefined => {
        if (!dateRange?.from) return undefined;
        
        return {
            from: startOfDay(dateRange.from),
            to: dateRange.to ? endOfDay(dateRange.to) : endOfDay(dateRange.from),
        };
    };

    const handleApply = () => {
        const normalized = normalizeRange(range);
        onChange(normalized);
        setIsOpen(false);
    };

    const handleClear = () => {
        setRange(undefined);
        onChange(undefined);
    };

    const handlePresetClick = (presetRange: DateRange) => {
        const normalized = normalizeRange(presetRange);
        setRange(normalized);
        onChange(normalized);
        setIsOpen(false);
    };

    const formatDisplayValue = () => {
        if (!value?.from) return 'Select date range';
        if (!value.to) return format(value.from, 'MMM dd, yyyy');
        return `${format(value.from, 'MMM dd, yyyy')} - ${format(value.to, 'MMM dd, yyyy')}`;
    };

    return (
        <div className="relative" ref={containerRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex w-full items-center justify-between gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm transition-all hover:border-stone-400 focus:outline-none focus:ring-2 focus:ring-[#e6186c] sm:w-auto sm:min-w-[280px]"
            >
                <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-stone-500" />
                    <span className={value?.from ? 'text-stone-900 font-medium' : 'text-stone-500'}>
                    {formatDisplayValue()}
                  </span>
                </div>

                {value?.from && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClear();
                        }}
                        className="rounded p-0.5 hover:bg-stone-100"
                    >
                        <XMarkIcon className="h-4 w-4 text-stone-400" />
                    </button>
                )}
            </div>

            {isOpen && (
                <div className="absolute right-0 z-50 mt-2 rounded-xl border border-stone-200 bg-white shadow-2xl">
                    <div className="flex">
                        {presets && (
                            <div className="w-40 border-r border-stone-200 p-3">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
                                    Quick Select
                                </p>
                                <div className="space-y-1">
                                    {quickPresets.map((preset) => (
                                        <button
                                            key={preset.label}
                                            type="button"
                                            onClick={() => handlePresetClick(preset.range)}
                                            className="block w-full rounded-md px-3 py-2 text-left text-sm text-stone-700 transition-colors hover:bg-pink-50 hover:text-[#e6186c]"
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="p-4">
                            <DayPicker
                                mode="range"
                                selected={range}
                                onSelect={setRange}
                                numberOfMonths={2}
                                disabled={{ after: today }}
                                components={{
                                    Chevron: ({ orientation }) => {
                                        const Icon = orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon;
                                        return <Icon className="h-4 w-4 absolute top-1" />;
                                    }
                                }}

                                classNames={{
                                    months: 'flex gap-6',
                                    month: 'space-y-4',
                                    caption: 'flex justify-between items-center mb-4 px-2',
                                    caption_label: 'text-sm font-semibold text-stone-900',
                                    nav: 'flex items-center gap-1 relative h-full',
                                    nav_button: 'h-8 w-8 bg-white border border-stone-200 p-0 hover:bg-pink-50 hover:border-[#e6186c] rounded-lg transition-colors flex items-center justify-center text-stone-600 hover:text-[#e6186c]',
                                    nav_button_previous: '',
                                    nav_button_next: '',
                                    table: 'w-full border-collapse',
                                    head_row: 'flex',
                                    head_cell: 'text-stone-500 rounded-md w-9 font-normal text-[0.8rem]',
                                    row: 'flex w-full mt-2',
                                    cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
                                    day: 'h-9 w-9 p-0 font-normal rounded-md hover:bg-pink-50 hover:text-[#e6186c] transition-colors',
                                    day_selected: 'bg-[#e6186c] text-white hover:bg-[#d01560] hover:text-white focus:bg-[#e6186c] focus:text-white',
                                    day_today: 'bg-pink-50 text-[#e6186c] font-semibold ring-1 ring-inset ring-pink-200',
                                    day_outside: 'text-stone-400 opacity-50',
                                    day_disabled: 'text-stone-400 opacity-50 cursor-not-allowed',
                                    day_range_middle: 'bg-pink-50 text-[#e6186c] rounded-none',
                                    day_range_start: 'bg-[#e6186c] text-white rounded-r-none',
                                    day_range_end: 'bg-[#e6186c] text-white rounded-l-none',
                                    day_hidden: 'invisible',
                                }}
                            />

                            <div className="mt-4 flex items-center justify-end gap-x-4 border-t border-stone-200 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="rounded-lg px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 border border-stone-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleApply}
                                    disabled={!range?.from}
                                    className="rounded-lg bg-[#e6186c] px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-[#d01560] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;